import React, { useState, useEffect, Fragment } from 'react';
import { RouteComponentProps, useLocation } from '@reach/router';
import billingInfoValidationRules from './BillingInfoForm.rules';
import {
  Box,
  Typography,
  useTheme,
  Grid,
  Link,
  Divider,
} from '@material-ui/core';
import useContentMgmt from '../../../hooks/useContentMgmt';
import microcopyGroupIds from '../../pge-plus-common/microcopyGroupIds';
import { navigate } from 'gatsby';
import routes from '../../../routes';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import colors from '../../../themes/main-colors';
import { GridItem } from '../../pge-plus-common/Grid';
import { parsePhoneNumber } from '../../../util/format';
import UpdateBillinginfo from './UpdateBillinginfo';
import {
  Application,
  ApplicationEv,
  ApplicationType,
} from '../../../__generated__/pgeplus-types';
import { getPOPUrl } from './utils/navigation.util';
import { BillingInfo, UpdateBillingInfo } from './pop.types';
import useFormState from '../../../hooks/useFormState';
import PGEButton from '../../buttons';
import Modal from '../../pge-plus-common/Modal';
import GoPaperLessIcon from '../../../static/goPaperLessIcon.svg';
import MailBillIcon from '../../../static/mailBillIcon.svg';
import { Question } from '../../pge-plus-common/Typography';
import CommonButton from '../../pge-plus-common/CommonButton';
import { usePaperlessBillForm } from '../../paperless-bill-form/usePaperlessBillForm';
import useAccountCustomer from '../../../hooks/useAccountCustomer';
import Backdrop from '../../backdrop/Backdrop';
import useUtil from '../../../hooks/pge-plus/useUtil';
import useWrapWithLoader from '../../../hooks/useWrapWithLoading';
import { useEVChargerFormState } from '../../../providers/EVChargerFormStateProvider';
import useWelcomeBackApi from '../../../hooks/pge-plus/useWelcomeBackApi';
import useIsPaperlessBillEnrolled from '../../../hooks/useIsPaperlessBillEnrolled';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    details: {
      fontSize: '20px',
      margin: '1.5rem 0px',
      [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
        fontSize: '16px',
      },
    },
    editLink: {
      fontSize: '16px',
      color: colors.sparkBlue,
      fontWeight: 700,
      cursor: 'pointer',
    },
    accept: {
      color: colors.white,
      minWidth: '130px',
      border: '2px solid #27788C',
      background: colors.sparkBlue,
      borderRadius: '30px',
      '&:hover': {
        backgroundColor: colors.buttonHoverBackground,
        borderColor: colors.buttonHoverBackground,
      },
      [theme.breakpoints.down('md')]: {
        width: '100%',
      },
    },

    back: {
      minWidth: '130px',
      height: '40px',
      borderRadius: '30px',
      [theme.breakpoints.up('sm')]: {
        marginRight: '15px',
      },
    },
    contactData: {
      fontWeight: 700,
      wordBreak: 'break-word',
      fontSize: '20px',
    },
    modalBody: {
      marginTop: theme.spacing(1),
      '& > *': {
        marginTop: theme.spacing(1),
      },
    },
    personalInfoLink: {
      margin: '16px 10%',
      color: colors.sparkBlue,
      fontWeight: 600,
      fontSize: '20px',
    },
    contactDataModal: {
      border: `1px solid ${colors.sparkBlue}`,
      borderRadius: 5,
      padding: 20,
      fontSize: 16,
      boxShadow: 'rgba(0, 0, 0, 0.24) 0px 3px 8px',
    },
    divider: {
      color: colors.lightGray1,
    },
    blueBox: {
      backgroundColor: colors.backgroundLightBlue,
      marginTop: theme.spacing(1),
      padding: theme.spacing(1.5),
      '& .MuiTextField-root .MuiFormHelperText-contained.Mui-error': {
        backgroundColor: colors.backgroundLightBlue,
      },
    },
  }),
);

type Props = RouteComponentProps & {
  application: Application;
  billingInfo: BillingInfo;
};

const ConfirmBillingAddress: React.FC<Props> = ({
  application,
  billingInfo,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const { customer } = useAccountCustomer();
  const [editBillingInfo, setEditBillingInfo] = useState<boolean>(false);

  const {
    formState: { application: evApplication },
    updateFormState,
  } = useEVChargerFormState();
  const { getApplicationByApplicationNo } = useWelcomeBackApi();

  type locationState = { forceFullPayment?: boolean };
  const { state } = useLocation();
  const forceFullPayment = (state as locationState)?.forceFullPayment === true;
  const [showModal, setShowModal] = useState(forceFullPayment);
  const { content, inlineRichText } = useContentMgmt(
    microcopyGroupIds.PGE_PLUS_POP_CONFIRM_BILLING_INFORMATION,
  );

  const billingInfoForm = useFormState(billingInfo, {
    validate: billingInfoValidationRules,
    validationContext: {
      content,
    },
  });

  const submitPaperlessService = usePaperlessBillForm();
  const { setErrorNotification } = useUtil();
  const { wrapWithLoader } = useWrapWithLoader();

  const {
    isPaperlessBillEnrolled,
    loading: isPaperlessBillingLoading,
  } = useIsPaperlessBillEnrolled(
    customer?.encryptedPersonId || '',
    application?.encryptedPgePlusAccountId || '',
  );

  useEffect(() => {
    if (billingInfo) {
      billingInfoForm.reset({
        ...billingInfo,
        goPaperLess: isPaperlessBillEnrolled,
      });
    }
  }, [billingInfo, isPaperlessBillEnrolled]);

  const setUpdatedValues = (updatedValues: UpdateBillingInfo) => {
    return false;
  };

  const handleSubmit = wrapWithLoader(async (billingInfoData: BillingInfo) => {
    if (billingInfoData.goPaperLess === isPaperlessBillEnrolled) {
      return navigate(
        getPOPUrl(
          routes.PGE_PLUS_PAY_OUT_OF_POCKET_PAYMENT_INFO,
          application?.id,
        ),
      );
    }

    try {
      const result = await submitPaperlessService.submitPaperless(
        customer?.encryptedPersonId || '',
        application?.encryptedPgePlusAccountId,
        !!billingInfoData.goPaperLess,
        customer?.email || '',
      );

      if (result?.success) {
        return navigate(
          getPOPUrl(
            routes.PGE_PLUS_PAY_OUT_OF_POCKET_PAYMENT_INFO,
            application?.id,
          ),
        );
      } else {
        setErrorNotification(true);
      }
    } catch (e) {
      setErrorNotification(true);
    }
  });

  const handleClickGoToQuote = wrapWithLoader(async () => {
    const nextRoute = getPOPUrl(
      routes.PGE_PLUS_PAY_OUT_OF_POCKET_CONFIRM_BILLING_ADDRESS,
      application?.id,
    );

    if (application) {
      if (application.applicationType === ApplicationType.Ev) {
        const { data: appData } = await getApplicationByApplicationNo({
          applicationId: application.applicationNo,
        });
        if (appData?.getApplication?.application) {
          updateFormState(
            'application',
            appData?.getApplication?.application as ApplicationEv,
          );
          void navigate(routes.PGE_PLUS_EV_CHARGERS_VIEW_EV_QUOTE, {
            state: { nextRoute },
          });
        }
      }
    }
  });

  return (
    <Fragment>
      {isPaperlessBillingLoading && <Backdrop forceOpen />}
      <form
        onSubmit={billingInfoForm.submit(
          values => handleSubmit(values),
          console.log,
        )}
        noValidate
      >
        <Box>
          <Grid
            container
            spacing={2}
            justify="center"
            alignItems="center"
            direction="column"
            css={{
              [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
                display: 'block',
              },
            }}
          >
            <Typography css={{ margin: '1rem 0rem' }} variant="h1">
              {content.get('CONFIRM_BILLING_ADDRESS_TITLE')}
            </Typography>
          </Grid>
          <Typography className={classes.details}>
            {content.get('CONFIRM_BILLING_ADDRESS_TEXT')}
          </Typography>
        </Box>
        <Box display={'flex'} justifyContent={'center'}>
          <Grid className={classes.contactDataModal} xs={12} md={9}>
            <Grid
              container
              xs={12}
              md={12}
              css={{ marginBottom: theme.spacing(1), display: 'flex' }}
              justify="space-between"
            >
              <Typography
                variant={'body1'}
                gutterBottom
                className={classes.contactData}
                style={{ paddingRight: '24px' }}
              >
                {content.get('CONFIRM_BILLING_CONTACT_INFO_TITLE')}
              </Typography>

              <Typography
                className={classes.editLink}
                onClick={() => setEditBillingInfo(true)}
              >
                {content.get('REUSABLE_EDIT')}
              </Typography>
            </Grid>

            <Divider className={classes.divider} sx={{ my: 2 }} />

            <Box className={classes.modalBody}>
              <Grid container css={{ marginTop: theme.spacing(2) }}>
                <Grid item xs={3}>
                  {/* <Typography variant="body1">{content.get('NAME')}:</Typography> */}
                  <Typography variant="body1" css={{ fontSize: 20 }}>
                    {content.get('REUSABLE_NAME') || ''}:
                  </Typography>
                </Grid>
                <Grid item xs={9}>
                  <Typography variant="body1" className={classes.contactData}>
                    {billingInfoForm.values?.name}
                  </Typography>
                </Grid>
              </Grid>
              <Grid container css={{ marginTop: theme.spacing(2) }}>
                <Grid item xs={3}>
                  {/* <Typography variant="body1">{content.get('ADDRESS')}:</Typography> */}
                  <Typography variant="body1" css={{ fontSize: 20 }}>
                    {content.get('REUSABLE_ADDRESS') || ''}:
                  </Typography>
                </Grid>
                <Grid item xs={9}>
                  <Typography variant="body1" className={classes.contactData}>
                    {billingInfoForm?.values?.addressLine1}
                  </Typography>
                </Grid>
              </Grid>
              <Grid container css={{ marginTop: theme.spacing(2) }}>
                <Grid item xs={3}>
                  {/* <Typography variant="body1">{content.get('PHONE')}:</Typography> */}
                  <Typography variant="body1" css={{ fontSize: 20 }}>
                    {content.get('REUSABLE_PHONE') || ''}:
                  </Typography>
                </Grid>
                <Grid item xs={9}>
                  <Typography variant="body1" className={classes.contactData}>
                    {billingInfoForm?.values?.phoneNumber}
                  </Typography>
                </Grid>
              </Grid>
              <Grid container css={{ marginTop: theme.spacing(2) }}>
                <Grid item xs={3}>
                  <Typography variant="body1" css={{ fontSize: 20 }}>
                    {content.get('REUSABLE_EMAIL') || ''}:
                  </Typography>
                </Grid>
                <Grid item xs={9}>
                  <Typography variant="body1" className={classes.contactData}>
                    {billingInfoForm?.values?.emailAddress}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Box>

        <Box display={'flex'} justifyContent={'center'}>
          <Grid css={{ margin: '16px 0 24px 0' }} xs={12} md={9}>
            <GridItem>
              <Link
                variant="body1"
                href={routes.PRIVACY_POLICY}
                target="_blank"
                css={{
                  fontSize: '20px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  '&:hover': {
                    textDecoration: 'none',
                  },
                  [theme.breakpoints.down('xs')]: {
                    fontSize: 16,
                  },
                }}
              >
                {content.get('HOW_WE_USE_YOUR_PERSONAL_INFORMATION')}
              </Link>
            </GridItem>
          </Grid>
        </Box>

        <Fragment>
          <Box display={'flex'}>
            <Box>
              <Question title={content.get('BILLING_PREFERENCE')} />
            </Box>
          </Box>
          <Grid container spacing={2} css={{ marginTop: 24 }} justify="center">
            <Grid
              item
              md={4}
              xs={6}
              css={{ display: 'flex', justifyContent: 'center' }}
            >
              <CommonButton
                buttonLabel={content.get('GO_PAPERLESS')}
                handleOnClick={(e: React.MouseEvent<HTMLElement>) =>
                  billingInfoForm.setValue('goPaperLess', true)
                }
                isActive={
                  !isPaperlessBillingLoading &&
                  !!billingInfoForm.values.goPaperLess
                }
                img={<GoPaperLessIcon css={{ width: 'auto !important' }} />}
              />
            </Grid>
            <Grid item md={4} xs={6}>
              <CommonButton
                buttonLabel={content.get('MAIL_BILL')}
                handleOnClick={(e: React.MouseEvent<HTMLElement>) =>
                  billingInfoForm.setValue('goPaperLess', false)
                }
                isActive={
                  !isPaperlessBillingLoading &&
                  billingInfoForm.values.goPaperLess === false
                }
                img={<MailBillIcon css={{ width: 'auto !important' }} />}
              />
            </Grid>
          </Grid>

          <Grid className={classes.blueBox}>
            {billingInfoForm.values.goPaperLess && (
              <Typography variant="body2">
                {inlineRichText(
                  content.get('PGE_PLUS_PAPERLESS_BILL_MESSAGE'),
                  {
                    email: billingInfoForm?.values?.emailAddress,
                  },
                )}
              </Typography>
            )}
            {billingInfoForm.values.goPaperLess === false && (
              <Typography variant="body2">
                {content.get('PGE_PLUS_MAILING_ADDRESS_MESSAGE')}
              </Typography>
            )}
          </Grid>

          <Grid
            container
            spacing={2}
            css={{
              marginTop: 24,
              marginBottom: 24,
              justifyContent: 'center',
            }}
          >
            <Grid item xs={6} md={3}>
              <PGEButton
                type="button"
                fullWidth
                variant="outlined"
                color="secondary"
                css={{
                  fontSize: '18px',
                  borderRadius: '30px',
                  '.MuiButton-label': {
                    fontWeight: 500,
                  },
                  '&:hover': {
                    borderWidth: '2px',
                  },
                }}
                onClick={handleClickGoToQuote}
              >
                {content.get('REUSABLE_BUTTON_BACK')}
              </PGEButton>
            </Grid>
            <Grid item xs={6} md={3}>
              <PGEButton
                type="submit"
                fullWidth
                variant="contained"
                css={{
                  fontSize: '18px',
                  borderRadius: '30px',
                  '.MuiButton-label': {
                    fontWeight: 500,
                  },
                }}
              >
                {content.get('REUSABLE_BUTTON_NEXT')}
              </PGEButton>
            </Grid>
          </Grid>
        </Fragment>

        {editBillingInfo && (
          <UpdateBillinginfo
            phoneNumber={parsePhoneNumber(billingInfo.phoneNumber)}
            emailAddress={billingInfo.emailAddress}
            consent={billingInfo.consent}
            open={editBillingInfo}
            content={content}
            onClose={() => setEditBillingInfo(false)}
            onUpdate={values => setUpdatedValues(values)}
          />
        )}
      </form>
      <Fragment>
        <Modal
          open={showModal}
          onClose={() => setShowModal(false)}
          title={content.get('ACTIVE_BANKRUPTCY_MODAL_TITILE')}
          showCloseIcon={true}
          modalStyles={{ outline: 'none', maxWidth: 560 }}
        >
          <Grid
            css={{
              display: 'flex',
              alignItems: 'flex-end',
              flexDirection: 'column',
            }}
          >
            <Typography
              variant="body1"
              css={{ padding: '1.5rem 0rem', fontSize: 20 }}
            >
              {content.get('ACTIVE_BANKRUPTCY_MODAL_CONTENT')}
            </Typography>
            <Box>
              <PGEButton
                fullWidth
                type={'button'}
                variant={'outlined'}
                color={'secondary'}
                className={classes.accept}
                onClick={() => setShowModal(false)}
              >
                {content.get('CONTINUE')}
              </PGEButton>
            </Box>
          </Grid>
        </Modal>
      </Fragment>
    </Fragment>
  );
};

export default ConfirmBillingAddress;
